import {
  Chip, CircularProgress, TableCell, TableRow, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import Moment from 'react-moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ACCOUNTS_STATUS_MAP, DATE_FORMAT } from '../../../../consts';
import STRING_KEYS from '../../../../language/keys';
import { AccountsActions } from '../../AccountActions';
import { CustomTooltip } from '../../../../components/CustomTooltip';
import Status from '../../../../components/Status';
import { useLoginAsAdmin } from '../../../../hooks/useLoginAsAdmin';
import useNavigate from '../../../../hooks/useNavigate';
import AccountPricingPlan from '../../../../components/AccountPricingPlan';
import { Account, LoginAsAdminResponse } from '../../../../@types/Account';
import style from './style';

type AccountRowItemProps = {
  account: Account,
  onDeleteHandler: (accountId: string) => void,
  onEditAccountClick: () => void,
};

export const AccountsRowItem = ({
  account,
  onDeleteHandler,
  onEditAccountClick,
}: AccountRowItemProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const { goToAccountView } = useNavigate();

  const [searchParams] = useSearchParams();

  const loginAsAdmin = useLoginAsAdmin(
    (loginAsAdminResponse: LoginAsAdminResponse) => {
      window.open(loginAsAdminResponse.frontendUrl, '_blank');
    },
  );

  const isActionInProgress = loginAsAdmin.isLoading;

  return (
    <TableRow
      key={ account.id + account.displayName }
      sx={ style.tableRow }
      hover
      onClick={ () => {
        goToAccountView(account.id, searchParams.toString(), btoa(searchParams.toString()));
      } }
    >
      <TableCell sx={ style.profileCell.sx }>
        <Box width={ 190 }>
          <CustomTooltip title={ `${account.displayName}` }>
            <Typography noWrap variant="subtitle2">
              {account.displayName}
            </Typography>
          </CustomTooltip>
          <CustomTooltip title={ `${account.id}` }>
            <Typography noWrap color="textSecondary" variant="body2">
              {account.id}
            </Typography>
          </CustomTooltip>
        </Box>
      </TableCell>

      <TableCell sx={ style.associatedCompanyCell.sx }>
        <CustomTooltip title={ account.companyName }>
          <Box>
            <Typography
              noWrap
              variant="body2"
              style={ style.associatedCompanyText.style }
            >
              {account.companyName}
            </Typography>
          </Box>
        </CustomTooltip>
      </TableCell>

      <TableCell sx={ style.dateCell.sx }>
        <CustomTooltip title={ account.subDomainName }>
          <AccountPricingPlan accountPlan={ account.plan } />
        </CustomTooltip>
      </TableCell>

      <TableCell sx={ style.statusCell.sx }>
        <Status
          label={ translate(STRING_KEYS.ACCOUNT_STATUSES[account.accountStatus]) }
          type={ ACCOUNTS_STATUS_MAP[account.accountStatus] }
        />
      </TableCell>

      <TableCell sx={ style.positionsAmountCell.sx }>
        <Chip
          label={ account.positionsAmount }
          variant={ style.chipCell.variant }
        />
      </TableCell>
      <TableCell sx={ style.dateCell.sx }>
        {account.updatedAt && (
          <Typography variant="caption">
            <Moment format={ DATE_FORMAT.TABLE_LIST }>{account.updatedAt}</Moment>
          </Typography>
        )}
      </TableCell>

      <TableCell sx={ style.dateCell.sx }>
        {account.createdAt && (
          <Typography variant="caption">
            <Moment format={ DATE_FORMAT.TABLE_LIST }>{account.createdAt}</Moment>
          </Typography>
        )}
      </TableCell>

      <TableCell sx={ style.menuCell.sx }
        onClick={ (e) => {
          e.stopPropagation();
        } }
      >
        {
          isActionInProgress ?
            <CircularProgress size={ 24 } /> :
            (
              <AccountsActions
                accountId={ account.id }
                accountName={ account?.displayName || '' }
                onDeleteHandler={ onDeleteHandler }
                onEditAccountClick={ onEditAccountClick }
                onLoginAsAdminClick={ () => {
                  loginAsAdmin.mutate({
                    accountId: account.id,
                  });
                } }
              />
            )
        }
      </TableCell>
    </TableRow>
  );
};
